import {pick} from '@benchling/js-utils';
import _ from 'underscore';

import {TagSchemaConstraint} from 'constants/RegistryConstants';
import {
  SchemaInterfaceFieldNode,
  TagSchemaConstraintNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {FieldType, InterfaceField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {omit} from 'util/omitKey';
import {MakeFieldsOptional} from 'util/Types';

export function coerceConstraintShape(
  interfaceConstraint: Omit<TagSchemaConstraintNode, 'id' | 'source'> | null
): Omit<TagSchemaConstraint, 'id' | 'source'> | null {
  return interfaceConstraint
    ? {
        ...omit(interfaceConstraint, '__typename', 'fieldsJson'),
        fields: interfaceConstraint.fieldsJson,
      }
    : null;
}

export type ClientInterfaceField = MakeFieldsOptional<InterfaceField, 'id' | 'apiId'>;

export const convertFromClientInterfaceFieldToSchemaField = (
  interfaceField: ClientInterfaceField
): ClientSchemaField => ({
  ...interfaceField,
  name: interfaceField.displayName,
  dbId: interfaceField.id,
  archiveRecord: null,
  cid: _.uniqueId(),
  isComputed: interfaceField.isComputed,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
});

type SchemaInterfaceFieldNodeAttributesUsedForClientInterfaceField = Pick<
  SchemaInterfaceFieldNode,
  | 'systemName'
  | 'displayName'
  | 'isRequired'
  | 'isMulti'
  | 'isComputed'
  | 'schemaFieldSelectorId'
  | 'requiredLinkJson'
  | 'fieldType'
>;

export const convertGQLSchemaInterfaceFieldToClientInterfaceField = <
  T extends Partial<SchemaInterfaceFieldNode> & SchemaInterfaceFieldNodeAttributesUsedForClientInterfaceField,
>(
  interfaceField: T
): ClientInterfaceField =>
  convertGQLSchemaInterfaceFieldToPartialClientInterfaceField(
    pick(
      interfaceField,
      'systemName',
      'displayName',
      'isRequired',
      'isMulti',
      'isComputed',
      'schemaFieldSelectorId',
      'requiredLinkJson',
      'fieldType',
      'apiId',
      'id'
    )
  );

// We can export this if there is a need for it in the future.
// Primarily it allows us to pass in a subset of attributes and receive a typesafe subset back.
const convertGQLSchemaInterfaceFieldToPartialClientInterfaceField = <
  T extends Partial<SchemaInterfaceFieldNode> & {
    requiredLinkJson: SchemaInterfaceFieldNode['requiredLinkJson'];
    fieldType: SchemaInterfaceFieldNode['fieldType'];
  },
>(
  interfaceField: T
): Omit<T, '__typename' | 'requiredLinkJson'> & {
  fieldType: FieldType;
  requiredLink: T['requiredLinkJson'];
} => ({
  ...omit(interfaceField, '__typename', 'requiredLinkJson'),
  fieldType: interfaceField.fieldType as FieldType,
  requiredLink: interfaceField.requiredLinkJson,
});

// This is a list of attributes on a ClientSchemaField that can be modified and maintained by a fieldset.
export const FIELDSET_CONTROLLED_FIELD_ATTRIBUTES: Array<keyof SchemaField> = [
  'archiveRecord',
  'name',
  'isRequired',
  'isMulti',
  'isComputed',
  'isParentLink',
  'fieldType',
  'systemName',
  'displayName',
  'requiredLink',
  'schemaFieldSelectorId',
];
